<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main.vue'
import Header from '@components/header.vue'
import FooterComponent from '@components/footer.vue'
import { store } from '../../state/store'
import JobService from '@services/business/job.service'
import Modal from '../../components/modal.vue'
import ErrorAlert from '@components/error-alert.vue'
import router from '../index'
import { roles, phoneNumbers } from '../../models/enums'
import AuthService from '@services/auth.service'

export default {
  page: {
    title: 'Job Complete',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: { Layout, Header, FooterComponent, Modal, ErrorAlert },
  data() {
    return {
      state: store.state,
      initialLoading: false,
      jobCompleteLoading: false,
      meridianOptions: [
        { text: 'AM', value: 'AM' },
        { text: 'PM', value: 'PM' },
      ],
      meridian: 'AM',
      jobCompleteOptions: [
        { text: 'Complete Job', value: true },
        { text: 'Reschedule Job', value: false },
      ],
      jobComplete: true,
      actualReason: '',
      jobResolution: '',
      causeCode: '',
      jobCompleteComments: '',
      showJobCommentModal: false,
      showJobCompleteModal: false,
      phone: '',
    }
  },
  computed: {
    isJobComplete: function() {
      return this.jobComplete
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.initialLoading = true
      this.getPhoneNumberForTechRole()
      JobService.getCodesForJobCompletion()
        .then(() => {
          this.initialLoading = false
        })
        .catch(() => {
          this.initialLoading = false
          store.presentErrorAlert('Unable to retrieve job codes.')
        })
    },
    getPhoneNumberForTechRole() {
      if (AuthService.HasRole(roles.BRINKS_TECH)) {
        this.phone = phoneNumbers.FIELD_TECH_DISPATCH
      } else if (
        AuthService.HasRole(roles.TECH_APP_ADMIN) ||
        AuthService.HasRole(roles.DEALER_TECH)
      ) {
        this.phone = phoneNumbers.SCHEDULING
      } else {
        this.phone = phoneNumbers.GENERAL
      }
    },

    setComplete() {
      // if reschedule then redirect to Job Search
      if (!this.isJobComplete) {
        store.unloadState()
        router.push('/')
        return
      }

      if (
        this.jobResolution !== '' &&
        this.causeCode !== '' &&
        this.actualReason !== ''
      ) {
        const completeJobDto = {
          resId: this.jobResolution,
          causeId: this.causeCode,
          probId: this.actualReason,
          jobComment: this.jobCompleteComments,
        }
        this.jobCompleteLoading = true
        JobService.completeJob(completeJobDto)
          .then((o) => {
            this.jobCompleteLoading = false
            this.closeJobCompleteModal()
            store.putSystemOffTest()

            router.push({
              name: 'job-complete-confirmation',
              params: {
                confirmationNumber: o.data.ConfirmationNumber,
              },
            })
          })
          .catch(() => {
            this.jobCompleteLoading = false
            store.presentErrorAlert(
              'Unable to complete job number: ' +
                this.state.currentJob.jobNumber +
                '.'
            )
          })
      } else {
        // TODO: replace this error alert with form validation once we complete that story
        store.presentErrorAlert(
          'Please fill out the required fields to complete the job.'
        )
      }
    },
    openJobCompleteModal() {
      this.showJobCompleteModal = true
    },
    closeJobCompleteModal() {
      this.showJobCompleteModal = false
    },
  },
}
</script>

<template>
  <Layout>
    <Header />
    <div :class="$style.job_complete_parent_container">
      <div v-show="initialLoading" :class="$style.spinner_container">
        <div class="spinner"></div>
      </div>
      <div v-show="!initialLoading" :class="$style.job_complete_container">
        <Modal v-if="showJobCompleteModal" @close="closeJobCompleteModal">
          <div slot="header">
            <h3 :class="$style.job_complete_modal_header">COMPLETE JOB</h3>
          </div>
          <div slot="body">
            <div
              v-show="jobCompleteLoading"
              :class="$style.modal_spinner_container"
            >
              <div class="spinner"></div>
            </div>
            <div v-show="!jobCompleteLoading">
              <ErrorAlert />
              <p
                >By completing this job, you confirm that you have
                <b>received a full set of signals.</b></p
              ><p
                >Any systems on test <b>will be taken off test</b> and you will
                no longer have access to this job.</p
              >
            </div>
          </div>
          <FooterComponent
            slot="footer"
            secondary-btn-type="link"
            secondary-btn-text="Cancel"
            :secondary-btn-callback="closeJobCompleteModal"
            primary-btn-text="CONFIRM"
            :primary-btn-callback="setComplete"
            :primary-btn-disable-state="jobCompleteLoading"
            primary-btn-id="btn-complete-job"
            :class="$style.footer_container_override"
          ></FooterComponent>
        </Modal>
        <ErrorAlert />
        <div class="form-group">
          <label>Resolution Type</label>
          <select
            v-model="jobComplete"
            class="form-control"
            name="job_complete"
          >
            <option
              v-for="option in jobCompleteOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.text }}
            </option>
          </select>
        </div>
        <div v-if="isJobComplete">
          <div class="form-group" :class="$style.required">
            <label>Actual Reason</label>
            <select
              v-model="actualReason"
              class="form-control"
              name="actual_reasons"
            >
              <option
                v-for="(option, i) in state.actualReasons"
                :key="'actualReason' + i"
                :value="option.problemId"
              >
                {{ option.description }}
              </option>
            </select>
          </div>
          <div :class="$style.required" class="form-group">
            <label>Job Resolution</label>
            <select
              v-model="jobResolution"
              class="form-control"
              name="job_resolutions"
            >
              <option
                v-for="(option, i) in state.currentJobResolutions"
                :key="'jobResolution' + i"
                :value="option.resolutionId"
              >
                {{ option.description }}
              </option>
            </select>
          </div>
          <div :class="$style.required" class="form-group">
            <label>Cause Code</label>
            <select v-model="causeCode" class="form-control" name="cause_codes">
              <option
                v-for="(option, i) in state.currentJobCauses"
                :key="'causeCode' + i"
                :value="option.jobCauseId"
              >
                {{ option.description }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label for="additional_job_notes">Job Comments</label>
            <textarea
              v-model="jobCompleteComments"
              class="form-control"
              name="additional_job_notes"
              rows="3"
              maxlength="225"
            ></textarea>
          </div>
        </div>
        <div v-else>
          <div :class="$style.dealer_services_container">
            <div :class="$style.dealer_services_label"
              >Call Dealer Services to Reschedule</div
            >
            <div class="py-2"
              ><a
                id="link-reschedule-job-phone"
                :class="$style.dealer_services_number"
                class="text-primary"
                :href="'tel:' + phone"
                >{{ phone }}</a
              ></div
            >
          </div>
        </div>
      </div>
      <FooterComponent
        v-if="jobComplete"
        slot="footer"
        primary-btn-text="COMPLETE"
        :primary-btn-callback="openJobCompleteModal"
      ></FooterComponent>
      <FooterComponent
        v-if="!jobComplete"
        slot="footer"
        primary-btn-text="JOB LOOKUP"
        :primary-btn-callback="setComplete"
        primary-btn-id="btn-reschedule-job"
      ></FooterComponent>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.dealer_services_label {
  font-weight: $heading-font-weight;
}

.job_complete_container {
  max-width: $size-parent-container-width-max;
  padding: 1em;
  margin: auto;
  text-align: left;
}

.job_complete_parent_container {
  position: relative;
  top: $height-status-bar;
  align-items: center;
  width: 100%;
  margin-bottom: $height-footer;
  text-align: center;
}

.meridian_header {
  font-size: 1em;
  font-weight: 900;
  color: $color-text-primary;
}

.enhance_radio_btn {
  transform: scale(2.5);
}

.dealer_services_container {
  margin: 1em;
  text-align: center;
}

a:visited {
  color: $color-text;
}

.close_button {
  float: right;
  font-size: 1.5em;
  color: $color-text-secondary;
  cursor: pointer;
  background-color: transparent;
  border: 0;
}

.close_button:hover {
  color: $color-text-primary;
}

.modal_title {
  color: $color-text-primary;
  font-weight: 900;
}

.required label::after {
  display: inline;
  color: #e32;
  content: ' *';
}

.job_complete_modal_header {
  margin-bottom: 0;
  color: $color-text-primary;
  font-weight: 900;
}

.footer_container_override {
  position: relative;
  height: auto;
  padding-top: 0.5em;
  background-color: $color-body-bg;
}

.dealer_services_number {
  font-size: 1.5em;
}

.spinner_container {
  position: fixed;
  top: 0;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}

.modal_spinner_container {
  margin: 4em;
}
</style>
